<template>
  <div
    aria-label="Formulaire de modification du personnel"
    role="main"
  >
    <b-card
      :title="null"
      aria-labelledby="page-title"
    >
      <h2
        id="page-title"
        class="sr-only"
      >
        Modification d'un membre du personnel
      </h2>
      <p class="sr-only">
        Tous les champs marqués d'un astérisque (*) sont obligatoires. Utilisez des informations professionnelles et logistiques précises.
      </p>
      <b-form
        ref="staffForm"
        aria-describedby="form-desc"
        @submit.prevent="updateStaff"
      >
        <!-- Sélection du rôle (déplacé en haut) -->
        <h5
          id="section-role"
          class="mb-2"
          aria-label="Sélection du rôle"
        >
          <feather-icon
            icon="ShieldIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Sélection du rôle
        </h5>

        <b-form-group
          label="Rôle *"
          label-for="role"
          description="Définissez le rôle logistique de ce membre (ex : Commercial, Magasinier, Support Client, Comptable)."
          :invalid-feedback="submitted && !form.role ? 'Le rôle est requis' : ''"
          :state="roleState"
        >
          <v-select
            id="role"
            v-model="form.role"
            :options="staffRoles"
            label="label"
            :reduce="role => role.value"
            placeholder="Sélectionnez un rôle"
            :class="[
              'enhanced-select',
              submitted && !form.role ? 'is-invalid' : '',
              form.role ? 'is-valid' : ''
            ]"
            :clearable="true"
            :searchable="true"
            aria-label="Sélection du rôle logistique"
          />
        </b-form-group>
        <div
          id="form-desc"
          class="sr-only"
        >
          Ce formulaire permet de modifier les informations d'un membre du personnel logistique. Veuillez vérifier chaque section avant de valider.
        </div>
        <!-- Informations personnelles -->
        <h5
          id="section-infos"
          class="mb-2"
          aria-label="Informations personnelles"
        >
          <feather-icon
            icon="UserIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Informations personnelles
        </h5>

        <b-row>
          <b-col md="6">
            <b-form-group
              label="Nom complet *"
              label-for="username"
              description="Nom et prénom du membre du personnel, tel qu'indiqué sur les documents officiels."
            >
              <b-form-input
                id="username"
                v-model="form.username"
                :state="nameState"
                placeholder="Entrez le nom complet"
                aria-required="true"
                aria-label="Nom complet"
              />
              <b-form-invalid-feedback v-if="submitted && !form.username">
                Le nom complet est requis pour l'identification logistique.
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group
              label="Adresse email professionnelle *"
              label-for="email"
              description="Utilisez l'adresse email professionnelle pour la communication interne."
            >
              <b-form-input
                id="email"
                v-model="form.email"
                type="email"
                :state="emailState"
                placeholder="Entrez l'adresse email professionnelle"
                aria-required="true"
                aria-label="Adresse email professionnelle"
              />
              <b-form-invalid-feedback v-if="submitted && !form.email">
                L'adresse email professionnelle est requise.
              </b-form-invalid-feedback>
              <b-form-invalid-feedback v-if="submitted && form.email && !emailState">
                Veuillez entrer une adresse email valide.
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group
              label="Téléphone principal"
              label-for="phone"
              description="Numéro de téléphone professionnel (8 chiffres, sans espaces)."
            >
              <b-form-input
                id="phone"
                v-model="form.phone"
                placeholder="Entrez le numéro de téléphone"
                type="tel"
                maxlength="8"
                :formatter="formatPhone"
                :state="phoneState"
                aria-label="Téléphone principal"
                @keypress="allowOnlyNumbers"
              />
              <b-form-invalid-feedback v-if="form.phone && !phoneState">
                Le numéro de téléphone doit contenir 8 chiffres.
              </b-form-invalid-feedback>
              <b-form-text>Format : 8 chiffres, sans espaces.</b-form-text>
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group
              label="Téléphone secondaire"
              label-for="phone2"
              description="Numéro de téléphone secondaire (optionnel)."
            >
              <b-form-input
                id="phone2"
                v-model="form.phone2"
                placeholder="Entrez le numéro de téléphone secondaire (optionnel)"
                type="tel"
                maxlength="8"
                :formatter="formatPhone"
                aria-label="Téléphone secondaire"
                @keypress="allowOnlyNumbers"
              />
              <b-form-text>Format : 8 chiffres, sans espaces.</b-form-text>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group
          label="Adresse professionnelle"
          label-for="address"
          description="Adresse complète du membre du personnel (rue, ville, code postal)."
        >
          <b-form-textarea
            id="address"
            v-model="form.address"
            placeholder="Entrez l'adresse complète"
            rows="3"
            aria-label="Adresse professionnelle"
          />
        </b-form-group>

        <b-form-group
          label="CIN (Carte d'Identité Nationale)"
          label-for="cin"
          description="Numéro de CIN à 8 chiffres (optionnel)."
        >
          <b-form-input
            id="cin"
            v-model="form.cin"
            placeholder="Entrez le numéro de CIN (optionnel)"
            maxlength="8"
            :formatter="formatCIN"
            :state="cinState"
            aria-label="Numéro de CIN"
            @keypress="allowOnlyNumbers"
          />
          <b-form-invalid-feedback v-if="form.cin && !cinState">
            Le numéro de CIN doit contenir 8 chiffres.
          </b-form-invalid-feedback>
          <b-form-text>Format : 8 chiffres, sans espaces.</b-form-text>
        </b-form-group>

        <!-- Affectation au dépôt -->
        <h5
          id="section-repo"
          class="mb-2 mt-4"
          aria-label="Affectation au dépôt"
        >
          <feather-icon
            icon="HomeIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Affectation au dépôt
        </h5>

        <b-form-group
          label="Dépôt *"
          label-for="repository"
          description="Sélectionnez le dépôt logistique auquel ce membre est affecté. Obligatoire pour les opérateurs de dépôt."
          :invalid-feedback="submitted && !form.repository ? 'Le dépôt est requis pour les opérateurs de dépôt' : ''"
          :state="repositoryState"
        >
          <v-select
            id="repository"
            v-model="form.repository"
            :options="repositories"
            label="name"
            :reduce="repo => repo.id"
            placeholder="Sélectionnez un dépôt"
            :class="[
              'enhanced-select',
              submitted && !form.repository ? 'is-invalid' : '',
              form.repository ? 'is-valid' : ''
            ]"
            :clearable="true"
            :searchable="true"
            :loading="repositoriesLoading"
            :disabled="repositoriesLoading"
            aria-label="Sélection du dépôt logistique"
            @input="onRepositoryChange"
          >
            <template #no-options>
              <span v-if="repositoriesLoading">Chargement des dépôts...</span>
              <span v-else>Aucun dépôt disponible</span>
            </template>
            <template #selected-option="{ name }">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="HomeIcon"
                  size="16"
                  class="mr-2"
                  style="color: #E31E24;"
                  aria-hidden="true"
                />
                <span>{{ name }}</span>
              </div>
            </template>
            <template #option="{ name }">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="HomeIcon"
                  size="16"
                  class="mr-2"
                  style="color: #E31E24;"
                  aria-hidden="true"
                />
                <span>{{ name }}</span>
              </div>
            </template>
            <template #spinner="{ loading }">
              <div
                v-if="loading"
                class="vs__spinner"
              >
                <div class="loading-dots">
                  <div class="dot1" />
                  <div class="dot2" />
                  <div class="dot3" />
                </div>
              </div>
            </template>
          </v-select>
        </b-form-group>

        <!-- Boutons d'action -->
        <div
          class="d-flex justify-content-between mt-4"
          aria-label="Actions du formulaire"
        >
          <b-button
            variant="outline-secondary"
            :to="{ name: 'staff-list' }"
            aria-label="Annuler et revenir à la liste du personnel"
          >
            <feather-icon
              icon="XIcon"
              size="16"
              class="mr-1"
              aria-hidden="true"
            />
            Annuler
          </b-button>
          <b-button
            variant="primary"
            type="submit"
            :disabled="isSubmitting"
            aria-label="Enregistrer les modifications"
          >
            <b-spinner
              v-if="isSubmitting"
              small
              class="mr-1"
              aria-hidden="true"
            />
            <feather-icon
              v-else
              icon="SaveIcon"
              size="16"
              class="mr-1"
              aria-hidden="true"
            />
            Enregistrer
          </b-button>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BRow,
  BCol,
  BSpinner,
  BFormInvalidFeedback,
  BFormText,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'StaffEdit',

  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BRow,
    BCol,
    BSpinner,
    BFormInvalidFeedback,
    BFormText,
    vSelect,
  },

  data() {
    return {
      isSubmitting: false,
      submitted: false,
      repositories: [],
      repositoriesLoading: true,
      form: {
        username: '',
        email: '',
        phone: '',
        phone2: '',
        address: '',
        cin: '',
        repository: null,
        role: null,
      },
      staffRoles: [
        { label: 'Commercial', value: 'sales' },
        { label: 'Magasinier', value: 'warehouse_operator' },
        { label: 'Support Client', value: 'customer_support' },
        { label: 'Comptable', value: 'accountant' },
      ],
    }
  },

  computed: {
    nameState() {
      return this.form.username ? true : null
    },

    emailState() {
      if (!this.form.email) return null
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(this.form.email)
    },

    phoneState() {
      if (!this.form.phone) return true
      return this.form.phone.length === 8 && /^\d+$/.test(this.form.phone)
    },

    repositoryState() {
      return this.form.repository ? true : null
    },

    cinState() {
      if (!this.form.cin) return true // Optional field
      return this.form.cin.length === 8 && /^\d+$/.test(this.form.cin)
    },

    roleState() {
      return this.form.role ? true : null
    },
  },

  created() {
    this.fetchRepositories()
    this.fetchStaffMember()
  },

  methods: {
    allowOnlyNumbers(event) {
      const { charCode, target } = event
      if (charCode < 48 || charCode > 57) {
        event.preventDefault()
      }
      if (target.id === 'phone' && this.form.phone && this.form.phone.length >= 8) {
        event.preventDefault()
      }
      if (target.id === 'phone2' && this.form.phone2 && this.form.phone2.length >= 8) {
        event.preventDefault()
      }
      if (target.id === 'cin' && this.form.cin && this.form.cin.length >= 8) {
        event.preventDefault()
      }
    },

    formatPhone(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    formatCIN(value) {
      return value.replace(/\D/g, '').substring(0, 8)
    },

    async fetchRepositories() {
      this.repositoriesLoading = true
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: 'Impossible de charger les dépôts',
        })
      } finally {
        this.repositoriesLoading = false
      }
    },

    async fetchStaffMember() {
      const staffId = this.$route.params.id
      try {
        const { data } = await axios.get(`/api/auth/staff-members/${staffId}/`)
        this.form = {
          username: data.username,
          email: data.email,
          phone: data.phone,
          phone2: data.phone2,
          address: data.address,
          cin: data.cin,
          repository: data.repository,
          role: data.role,
        }
      } catch (error) {
        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: 'Impossible de charger les informations du membre',
        })
      }
    },

    onRepositoryChange(value) {
      this.form.repository = value
      if (value && this.submitted) {
        this.submitted = false
        this.$nextTick(() => {
          this.submitted = true
        })
      }
    },

    validateForm() {
      this.submitted = true
      if (!this.form.username) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Le nom complet est requis',
        })
        return false
      }
      if (!this.form.email || !this.emailState) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Une adresse email valide est requise',
        })
        return false
      }
      // Only require repository for warehouse_operator
      if (this.form.role === 'warehouse_operator' && !this.form.repository) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Veuillez sélectionner un dépôt',
        })
        return false
      }
      if (!this.form.role) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Veuillez sélectionner un rôle',
        })
        return false
      }
      if (this.form.phone && !this.phoneState) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Le numéro de téléphone doit contenir 8 chiffres',
        })
        return false
      }
      if (this.form.cin && !this.cinState) {
        this.showToast({
          variant: 'danger',
          title: 'Validation échouée',
          text: 'Le numéro de CIN doit contenir 8 chiffres',
        })
        return false
      }
      return true
    },

    async updateStaff() {
      if (this.isSubmitting) return
      if (!this.validateForm()) return
      this.isSubmitting = true
      const staffId = this.$route.params.id
      try {
        // Ensure repository is an ID, not an object
        const payload = {
          ...this.form,
          repository: typeof this.form.repository === 'object' && this.form.repository !== null
            ? this.form.repository.id
            : this.form.repository,
          role: this.form.role,
        }
        await axios.put(`/api/auth/staff-members/${staffId}/update/`, payload)
        this.showToast({
          variant: 'success',
          title: 'Succès',
          text: 'Membre du personnel mis à jour avec succès',
        })
        setTimeout(() => {
          this.$router.push({ name: 'staff-list' })
        }, 1000)
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Erreur lors de la mise à jour du membre du personnel'
        this.showToast({
          variant: 'danger',
          title: 'Erreur',
          text: errorMessage,
        })
      } finally {
        this.isSubmitting = false
      }
    },

    showToast({ variant, title, text }) {
      let icon
      if (variant === 'success') {
        icon = 'CheckCircleIcon'
      } else if (variant === 'danger') {
        icon = 'AlertTriangleIcon'
      } else {
        icon = 'AlertCircleIcon'
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant,
        },
      }, {
        position: 'top-right',
        timeout: 5000,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';
</style>
